<template>
    <div class="dispatch-builder">
        <h2 class="content-block">Dispatch</h2>

        <!-- Step indicator — also the navigation: each pill jumps to its step. -->
        <div class="stepper content-block">
            <button v-for="step in steps" :key="step.id" type="button" class="step"
                :class="{ active: currentStep === step.id, done: currentStep > step.id }"
                :disabled="!canGoTo(step.id)" :title="stepTitle(step)" @click="goTo(step.id)">
                <span class="step-num">{{ step.number }}</span>
                <span class="step-label">{{ step.label }}</span>
            </button>
        </div>

        <DispatchStart v-if="currentStep === 1" @create-new="onCreateNew" @load="onLoad" />

        <DispatchParameters v-else-if="currentStep === 2" @previous="goTo(1)" @next="goTo(3)" />

        <DispatchSites v-else-if="currentStep === 3" @previous="goTo(2)" @next="goTo(afterSites)" />

        <DispatchSupply v-else-if="currentStep === 4" @previous="goTo(3)" @next="goTo(5)" />

        <DispatchSummary v-else-if="currentStep === 5" @previous="goTo(afterSites === 5 ? 3 : 4)"
            @done="onDone" />
    </div>
</template>

<script>
import DispatchStart from './dispatch-start.vue';
import DispatchParameters from './dispatch-parameters.vue';
import DispatchSites from './dispatch-sites.vue';
import DispatchSupply from './dispatch-supply.vue';
import DispatchSummary from './dispatch-summary.vue';

export default {
    name: 'DispatchBuilder',
    components: {
        DispatchStart,
        DispatchParameters,
        DispatchSites,
        DispatchSupply,
        DispatchSummary,
    },
    data() {
        return {
            currentStep: 1,
            // True once a dispatch is being worked on (created or loaded). The
            // later steps have nothing to show before that, so their pills stay
            // disabled until it flips.
            started: false,
            allSteps: [
                { id: 1, label: 'Start' },
                { id: 2, label: 'Parameters' },
                { id: 3, label: 'Sites' },
                { id: 4, label: 'Supply' },
                { id: 5, label: 'Review & Save' },
            ],
        };
    },
    computed: {
        // Supply only asks about totes. On a tanker run the product comes with the
        // hauler, so the step has nothing to collect and is dropped entirely —
        // the pills renumber so there is no gap where it used to be.
        isTotes() {
            return this.$store.getters['dispatch/getDeliveryMethod'] === 'totes';
        },
        steps() {
            return this.allSteps
                .filter((s) => s.id !== 4 || this.isTotes)
                .map((s, i) => ({ ...s, number: i + 1 }));
        },
        // Where Next goes from Sites, and where Previous comes back to from Review.
        afterSites() {
            return this.isTotes ? 4 : 5;
        },
    },
    created() {
        // Load recent dispatches and option data for the start/parameters screens,
        // plus the site/container data up front (which also feeds the distinct
        // product list) so later steps render immediately.
        this.$store.dispatch('dispatch/loadSavedDispatches');
        this.$store.dispatch('operators/loadOperators');
        // Which areas each operator actually works, used to scope the Area picker.
        this.$store.dispatch('operators/loadOperatorsAreas');
        this.$store.dispatch('areas/loadAreas');
        this.$store.dispatch('dispatch/loadContainersStatus', { typeId: 1 });
    },
    methods: {
        // Step 1 is always reachable (it's the entry point, and returning to it
        // doesn't discard the working dispatch — only "Create New" does).
        canGoTo(step) {
            return step === 1 || this.started;
        },
        stepTitle(step) {
            return this.canGoTo(step.id)
                ? `Go to ${step.label}`
                : 'Start or load a dispatch first';
        },
        goTo(step) {
            if (!this.canGoTo(step)) return;
            // Switching to a tanker run while sitting on Supply would strand the
            // user on a step that no longer exists.
            if (step === 4 && !this.isTotes) {
                this.currentStep = 5;
                return;
            }
            this.currentStep = step;
        },
        onCreateNew() {
            this.$store.dispatch('dispatch/startNewDispatch');
            this.started = true;
            this.currentStep = 2;
        },
        onLoad(dispatch) {
            // Loading an existing dispatch jumps straight to Review & Save,
            // skipping parameters, site selection and supply.
            this.$store.dispatch('dispatch/loadDispatch', dispatch);
            this.started = true;
            this.currentStep = 5;
        },
        onDone() {
            this.$store.dispatch('dispatch/startNewDispatch');
            this.started = false;
            this.currentStep = 1;
        },
    },
};
</script>

<style scoped>
h2 {
    margin-left: 0.5em;
}

.stepper {
    display: flex;
    gap: 8px;
    margin: 0 20px 16px 20px;
    flex-wrap: wrap;
}

.step {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border: none;
    border-radius: 18px;
    background: rgba(120, 120, 120, 0.12);
    color: inherit;
    font-family: inherit;
    opacity: 0.6;
    cursor: pointer;
    transition: background 0.15s, opacity 0.15s;
}

.step:hover:not(:disabled):not(.active) {
    opacity: 1;
    background: rgba(120, 120, 120, 0.24);
}

.step:disabled {
    cursor: default;
}

.step.active {
    opacity: 1;
    background: rgba(255, 115, 0, 0.18);
}

.step.done {
    opacity: 0.9;
}

.step-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: rgba(120, 120, 120, 0.4);
    font-size: 12px;
    font-weight: 600;
}

.step.active .step-num {
    background: rgb(255, 115, 0);
    color: #fff;
}

.step.done .step-num {
    background: #4caf50;
    color: #fff;
}

.step-label {
    font-size: 13px;
}
</style>
